import { Alert, Button, DialogContentText } from "@carrotfertility/carotene-core"
import { ConfirmationDialog } from "@carrotfertility/carotene-core-x"
import React, { useEffect, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { formatAppointmentDate, formatAppointmentTime, formatProviderName } from "./formatting"
import { useCancelAppointment } from "./useAppointments"
import type { UpcomingAppointment } from "./types"

/** Frame 6 asks, frame 7 reports. One dialog, two steps — the member never leaves it. */
type CancelStep = "confirm" | "canceled"

/**
 * The cancel flow, frames 6 and 7 of the S4 design.
 *
 * **Rendered by the page, not by the Up next card.** Confirming removes the appointment, which
 * unmounts the card — and frame 7 draws the canceled dialog over a page that has *already* dropped
 * its Up next section. A dialog living inside the card would go with it.
 *
 * That is also why the appointment arrives whole rather than as pre-formatted `providerName` /
 * `whenLabel` strings: it is the page's snapshot of what is being cancelled, and it has to outlive
 * that appointment's removal from the page data.
 *
 * Built on `ConfirmationDialog` from `carotene-core-x` rather than the raw `Dialog*` primitives: it
 * wires `aria-labelledby` to the title for us, and it renders no close affordance in either step,
 * which is what frame 7 asks for. Radius, 2rem padding, elevation-24 and the design's 600px cap all
 * come from the Carotene dialog theme untouched — `maxWidth="md"` is its default and Carotene's `md`
 * breakpoint is 600px.
 */
export function CancelAppointmentDialog({
  appointment,
  onClose
}: {
  /**
   * Nullable, and so is its `date` — the page's snapshot of a fixture/API record. Explicit here
   * because this package is `strict`, where the host it moved from was not; `CancelAppointmentDialog.test.tsx`
   * renders both.
   */
  appointment: UpcomingAppointment | null | undefined
  onClose: () => void
}): JSX.Element | null {
  const intl = useIntl()
  const [step, setStep] = useState<CancelStep>("confirm")
  const { mutate: cancelAppointment, isLoading, isError } = useCancelAppointment()
  const acknowledgeRef = useRef<HTMLButtonElement>(null)

  // Confirming unmounts the button the member just pressed, which drops their focus onto the body.
  // Moved by hand rather than with `autoFocus`, which `jsx-a11y/no-autofocus` rightly bans: the rule
  // is about stealing focus on load, and this is the opposite — restoring focus the step change took
  // away. Called before the guard below because a hook cannot run conditionally.
  useEffect(() => {
    if (step === "canceled") {
      acknowledgeRef.current?.focus()
    }
  }, [step])

  // Guard rather than take the page down with a missing appointment or a missing date on one.
  if (!appointment?.date) {
    return null
  }

  const providerName = formatProviderName(appointment.provider)
  // The date and time themselves still come from `intl` — `formatDate`/`formatTime` are Intl wrappers
  // with nothing to extract, so the member's own date format survives the English copy around it.
  const whenLabel = `${formatAppointmentDate(intl, appointment.date)} at ${formatAppointmentTime(
    intl,
    appointment.date
  )}`

  const confirmCancelation = (): void => {
    // Advance only on a confirmed success (the fixture calls back synchronously; the real mutation
    // after the writeback). A failure never advances — `isError` surfaces the message below instead.
    cancelAppointment(appointment.id, { onSuccess: () => setStep("canceled") })
  }

  // An explicit `switch`, per the convention `mock/scenarios.ts` sets: nothing selects a function
  // value by key, so CodeQL's `js/unvalidated-dynamic-method-call` has nothing to flow into, and the
  // `never` assignment makes the compiler reject a step added without a branch here.
  switch (step) {
    case "confirm":
      return (
        <ConfirmationDialog
          open
          onClose={onClose}
          title="Cancel this appointment?"
          // `Keep appointment` is the secondary slot, which puts it first in the DOM and so first in
          // the tab order. The design draws the destructive action to its left; Carotene's
          // `DialogActionsBar` fixes the order, and the way round it fixes is the one that does not
          // land a member's first keystroke on `Cancel appointment`.
          secondaryAction={
            <Button variant="outlined" color="secondary" onClick={onClose}>
              Keep appointment
            </Button>
          }
          primaryAction={
            <Button key="confirm" variant="contained" color="error" disabled={isLoading} onClick={confirmCancelation}>
              {isLoading ? "Cancelling…" : "Cancel appointment"}
            </Button>
          }
        >
          <DialogContentText>
            {`Your visit with ${providerName} on ${whenLabel} will be canceled. You can rebook anytime, though this slot may not stay open.`}
          </DialogContentText>
          {isError && (
            <Alert severity="error" sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.md) })}>
              We couldn&rsquo;t cancel your appointment. Please try again.
            </Alert>
          )}
        </ConfirmationDialog>
      )
    case "canceled":
      return (
        <ConfirmationDialog
          open
          onClose={onClose}
          title="Appointment canceled"
          // Frame 7 has a single action and no close X.
          //
          // The `key` is load-bearing, not decoration. Both steps put a `Button` in this same slot, so
          // without distinct keys React reconciles them as one node and relabels it in place — leaving
          // the member focused on a button that changed under them, which a screen reader does not
          // announce. A distinct key makes it a real unmount and mount, so the refocus above lands on
          // a newly announced action.
          primaryAction={
            <Button key="acknowledge" ref={acknowledgeRef} variant="contained" color="secondary" onClick={onClose}>
              Okay
            </Button>
          }
        >
          <DialogContentText>
            {`Your visit with ${providerName} on ${whenLabel} has been canceled. You can book a new visit whenever you're ready.`}
          </DialogContentText>
        </ConfirmationDialog>
      )
    default: {
      const unhandled: never = step
      throw new Error(`Unhandled cancel step: ${String(unhandled)}`)
    }
  }
}
